import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mcaI20Logo, mcaLogo } from "../../Assets";
import adminAuctionBg from "../../Assets/admin/auctionbg.png";
import KenLoader from "../../GlobalComponents/KenLoader";
import { useAuthStore } from "../../store";
import { getTeamDetails } from "../../Utils/apiService";
import routes from "../../Utils/routes";
import socket from "../../Utils/socketClient";
import { MCA_AUCTION_NAVIGATE_HOME } from "../../Helpers/constant";
import {
  resolveAuctionDivisionFromHints,
  resolveTeamDivision,
  type AuctionDivision,
} from "../../Utils/auctionRules";
import { LivePlayerPlaceholder } from "./components/LivePlayerPlaceholder";
import { PlayerPreview } from "./components/PlayerPreview";
import { PlayersTable } from "./components/PlayersTable";
import { TeamSidebar } from "./components/TeamSidebar";
import { stripe } from "./components/helpers";

type SortConfig = {
  key: string;
  direction: "asc" | "desc";
} | null;

const AdminLiveTeamDetails = () => {
  const navigate = useNavigate();
  const {
    liveBidData,
    liveTeamId,
    selectedPlayerData,
    teamDetailsTrigger,
    teamDetailsDivision,
  } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [teamDetails, setTeamDetails] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [selectedDivision, setSelectedDivision] =
    useState<AuctionDivision>("men");
  const [isSocketConnected, setIsSocketConnected] = useState(socket.connected);

  const activeDivision: AuctionDivision = selectedDivision;
  const handleHeaderLogoClick = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const homePath = params.get("embedHomePath") || routes.dashboard;
    const parentOrigin = params.get("embedParentOrigin") || "*";

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(MCA_AUCTION_NAVIGATE_HOME, parentOrigin);
      window.parent.postMessage(
        {
          type: MCA_AUCTION_NAVIGATE_HOME,
          payload: { path: homePath },
        },
        parentOrigin,
      );
      return;
    }

    navigate(homePath);
  }, [navigate]);

  const fetchTeamDetails = useCallback(
    async ({ showLoader }: { showLoader: boolean }) => {
      try {
        if (showLoader) {
          setLoading(true);
        }

        const teamsRes = await getTeamDetails();
        if (teamsRes?.data?.success) {
          setTeamDetails(teamsRes.data.data || []);
        } else {
          setTeamDetails([]);
        }
      } catch (err) {
        console.error("Error fetching team details:", err);
        setTeamDetails([]);
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    fetchTeamDetails({ showLoader: true });
  }, [fetchTeamDetails]);

  useEffect(() => {
    if (!teamDetailsTrigger) return;
    fetchTeamDetails({ showLoader: false });
  }, [fetchTeamDetails, teamDetailsTrigger]);

  useEffect(() => {
    const onConnect = () => {
      setIsSocketConnected(true);
    };

    const onDisconnect = () => {
      setIsSocketConnected(false);
    };

    const onConnectError = () => {
      setIsSocketConnected(false);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
    };
  }, []);

  const filteredTeams = useMemo(
    () =>
      teamDetails.filter(
        (item: any) => resolveTeamDivision(item) === activeDivision,
      ),
    [activeDivision, teamDetails],
  );

  const livePlayer = liveBidData?.player
    ? { ...(selectedPlayerData || {}), ...(liveBidData.player || {}) }
    : selectedPlayerData;

  const liveTeamForPreview = useMemo(
    () =>
      teamDetails.find(
        (team: any) =>
          team?.Id === liveTeamId ||
          team?.Id === livePlayer?.soldToId ||
          team?.Name === livePlayer?.teamName ||
          team?.Name === livePlayer?.soldTo,
      ),
    [
      teamDetails,
      livePlayer?.soldTo,
      livePlayer?.soldToId,
      livePlayer?.teamName,
      liveTeamId,
    ],
  );

  const previewDivision = useMemo(
    () =>
      resolveAuctionDivisionFromHints({
        gender: livePlayer?.gender,
        divisionHint: teamDetailsDivision,
        team: liveTeamForPreview,
      }),
    [livePlayer?.gender, liveTeamForPreview, teamDetailsDivision],
  );

  useEffect(() => {
    if (
      selectedPlayerData?.auctionResult !== "Sold" ||
      !selectedPlayerData?.soldToId
    ) {
      return;
    }

    const playerDetails = selectedPlayerData?.playerDetails || {};
    const playerName =
      `${selectedPlayerData?.firstName || ""} ${selectedPlayerData?.lastName || ""}`.trim() ||
      selectedPlayerData?.name ||
      playerDetails?.name ||
      playerDetails?.Player__r?.Name ||
      "-";
    const playerRegistrationId =
      playerDetails?.playerRegId ||
      selectedPlayerData?.playerRegId ||
      playerDetails?.Id ||
      selectedPlayerData?.MCANo ||
      playerName;

    const patchedPlayer = {
      ...playerDetails,
      Id: playerRegistrationId,
      Player__r: {
        ...(playerDetails?.Player__r || {}),
        Name: playerName,
        Photo_URL__c:
          selectedPlayerData?.photo ||
          playerDetails?.photo ||
          playerDetails?.Player__r?.Photo_URL__c,
      },
      Primary_Role__c:
        selectedPlayerData?.roleType ||
        playerDetails?.roleType ||
        playerDetails?.role ||
        playerDetails?.Primary_Role__c,
      Recent_Competitive_Level__c:
        selectedPlayerData?.highest_level_played ||
        playerDetails?.recentCompetetionLevel ||
        playerDetails?.recentCompetitionLevel ||
        playerDetails?.Recent_Competitive_Level__c,
      Winning_Bid__c:
        selectedPlayerData?.winningBid ??
        playerDetails?.Winning_Bid__c ??
        playerDetails?.winningBid,
    };

    setTeamDetails((currentTeams) =>
      currentTeams.map((team: any) => {
        const currentRecords = team?.Player_Registrations__r?.records || [];
        const recordsWithoutPlayer = currentRecords.filter(
          (record: any) => record?.Id !== playerRegistrationId,
        );

        if (team?.Id !== selectedPlayerData.soldToId) {
          return {
            ...team,
            Player_Registrations__r: {
              ...(team?.Player_Registrations__r || {}),
              records: recordsWithoutPlayer,
            },
          };
        }

        return {
          ...team,
          Player_Registrations__r: {
            ...(team?.Player_Registrations__r || {}),
            records: [patchedPlayer, ...recordsWithoutPlayer],
          },
        };
      }),
    );
  }, [selectedPlayerData]);

  useEffect(() => {
    setSelectedTeam((currentTeam: any) => {
      const liveTeam = filteredTeams.find(
        (team: any) =>
          team?.Id === liveTeamId ||
          team?.Id === livePlayer?.soldToId ||
          team?.Name === livePlayer?.teamName ||
          team?.Name === livePlayer?.soldTo,
      );

      if (liveTeam) return liveTeam;

      if (
        currentTeam &&
        filteredTeams.some((team: any) => team?.Id === currentTeam?.Id)
      ) {
        return (
          filteredTeams.find((team: any) => team?.Id === currentTeam?.Id) ||
          currentTeam
        );
      }

      return filteredTeams[0] || null;
    });
  }, [
    filteredTeams,
    livePlayer?.soldTo,
    livePlayer?.soldToId,
    livePlayer?.teamName,
    liveTeamId,
  ]);

  if (loading) {
    return <KenLoader />;
  }

  const team = selectedTeam || filteredTeams[0];
  const players = team?.Player_Registrations__r?.records || [];

  const sortedPlayers = [...players].sort((a: any, b: any) => {
    if (!sortConfig?.key) return 0;
    const aVal = (a?.Player__r?.Name || "").toLowerCase();
    const bVal = (b?.Player__r?.Name || "").toLowerCase();
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const teamMeta = [
    { label: "Coach", value: "Sangalp N" },
    { label: "Owner", value: "Ram Jay" },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "100%", md: "100dvh" },
        minHeight: { xs: 0, md: "unset" },
        color: "#fff",
        px: 0,
        boxSizing: "border-box",
        overflow: "hidden",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "sticky",
          top: 0,
          zIndex: 20,
          flexShrink: 0,
          // border: "1px solid rgba(255,255,255,0.14)",
          backgroundColor: "#050505",
          mb: { xs: 0.6, md: "0.35vw" },
        }}
      >
        <Box
          sx={{
            height: { xs: 70, md: "5vw" },
            minHeight: { md: 92 },
            px: { xs: 1.3, sm: 2.2, md: "1.8vw" },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#000",
          }}
        >
          <Box
            component="img"
            src={mcaLogo}
            alt="MCA"
            onClick={handleHeaderLogoClick}
            sx={{
              width: { xs: 50, md: "4vw" },
              minWidth: { md: 44 },
              minHeight: { md: 44 },
              borderRadius: "50%",
              objectFit: "cover",
              backgroundColor: "#fff",
              cursor: "pointer",
            }}
          />
          <Box
            component="img"
            src={mcaI20Logo}
            alt="T20 Mumbai"
            sx={{
              width: { xs: 60, md: "4vw" },
              minWidth: { md: 44 },

              minHeight: { md: 44 },
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        </Box>
        {!isSocketConnected && (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: 11,
              fontWeight: 600,
              color: "#FFB74D",
              backgroundColor: "rgba(255,183,77,0.12)",
              borderTop: "1px solid rgba(255,183,77,0.35)",
            }}
          >
            Live socket unavailable. Syncing via auto-refresh.
          </Box>
        )}
        <Box
          sx={{
            width: "100%",
            height: { xs: 3 },
            minHeight: { md: 4 },
            background: stripe,
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: 0,
          boxSizing: "border-box",
          overflow: "hidden",
          overflowX: "hidden",
          backgroundColor: "#000",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 0.8, md: 0 },
            alignItems: "stretch",
            height: "100%",
            minHeight: 0,
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "68.5%" },
              maxWidth: { xs: "100%", md: "68.5%" },
              height: { xs: "auto", md: "100%" },
              minHeight: { xs: 0, md: 0 },
              flexShrink: { xs: 0, md: 1 },
              minWidth: 0,
              backgroundImage: `url(${adminAuctionBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              display: "flex",
              flexDirection: "column",
              position: { xs: "static", md: "sticky" },
              top: { xs: "auto", md: "0.8vw" },
              alignSelf: { xs: "auto", md: "flex-start" },
              zIndex: { xs: "auto", md: 2 },
              maxHeight: { xs: "none", md: "100%" },
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {livePlayer ? (
                <PlayerPreview
                  player={livePlayer}
                  liveBidData={liveBidData}
                  division={previewDivision}
                />
              ) : (
                <LivePlayerPlaceholder />
              )}
            </Box>
          </Box>

          <TeamSidebar
            activeDivision={activeDivision}
            onDivisionChange={setSelectedDivision}
            filteredTeams={filteredTeams}
            selectedTeam={team}
            onTeamSelect={setSelectedTeam}
            players={players}
            teamMeta={teamMeta}
          >
            <PlayersTable
              sortedPlayers={sortedPlayers}
              setSortConfig={setSortConfig}
              activeDivision={activeDivision}
            />
          </TeamSidebar>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLiveTeamDetails;
